import axios from "axios";
// import { getMaximumSize } from "chart.js/helpers";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");
    const tokenType =
      localStorage.getItem("token_type") ||
      sessionStorage.getItem("token_type") ||
      "Bearer";
    if (token) {
      config.headers.Authorization = `${tokenType} ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ---------- Session renewal ----------
// Access tokens last 30 minutes; renew them quietly with the 7-day refresh
// token instead of throwing the student back to the login page.
const tokenStore = () =>
  sessionStorage.getItem("refresh_token") && !localStorage.getItem("refresh_token")
    ? sessionStorage
    : localStorage;

const clearSessionAndLogin = () => {
  ["access_token", "refresh_token", "token_type", "role"].forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
};

let refreshing = null; // one renewal at a time, shared by concurrent requests

export const refreshAccessToken = () => {
  if (refreshing) return refreshing;
  const refreshToken =
    localStorage.getItem("refresh_token") || sessionStorage.getItem("refresh_token");
  if (!refreshToken) return Promise.resolve(null);

  refreshing = axios
    .post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken })
    .then((res) => {
      const token = res.data?.data?.access_token || res.data?.access_token;
      if (token) tokenStore().setItem("access_token", token);
      return token || null;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

// Response interceptor: on an expired token, renew once and retry the request
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const isAuthCall = /\/auth\/(login|refresh|signup)/.test(original.url || "");
    if (error.response?.status === 401 && !original._retried && !isAuthCall) {
      original._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${token}` };
        return api(original);
      }
      clearSessionAndLogin();
    }
    return Promise.reject(error);
  }
);

// Renew the session in the background shortly before it expires, so pages
// that call the backend with fetch() keep working too
const scheduleRenewal = () => {
  const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  const payload = token ? decodeJWT(token) : null;
  if (!payload?.exp) return;
  const msLeft = payload.exp * 1000 - Date.now() - 2 * 60 * 1000;
  setTimeout(async () => {
    if (await refreshAccessToken()) scheduleRenewal();
  }, Math.max(msLeft, 5000));
};

export const agentService = {
  getTutorResponse: async (message, userId, options = {}) => {
    const payload = {
      message,
      user_id: userId,
      student_id: options.studentId,
      teacher_id: options.teacherId,
      session_id: options.sessionId,
      force_new_session: options.forceNewSession || false,
      subject: options.subject,
      topic: options.topic,
    };
    return await api.post("/agents/tutor/chat", payload);
  },

  getQuizRecommendations: async (studentId) => {
    return await api.post("/agents/quiz", { studentId });
  },

  getProgressAnalytics: async (studentId) => {
    return await api.post("/agents/analytics", { studentId });
  },
  getStudyPlan: async (studentId) => {
    return await api.post("/agents/studyplan", { studentId });
  },

  getFeedback: async (content, userId) => {
    return await api.post("/agents/feedback", { content, userId });
  },
  getSummarization: async (content) => {
    return await api.post("/agents/summarization", { content });
  },
  getRecommendation: async (topic) => {
    return await api.post("/agents/recommendation", { topic });
  },
};

export const authService = {
  login: async (credentials) => {
    const response = await api.post("/auth/login", credentials);
    if (response.data.token) {
      localStorage.setItem("token", response.data.token);
    }
    return response.data;
  },

  register: async (userData) => {
    return await api.post("/auth/register", userData);
  },

  logout: () => {
    localStorage.removeItem("token");
  },
};

export const userService = {
  getProfile: async () => {
    // Import dynamically to avoid circular dependencies
    const { getMyProfile } = await import("./profileService.js");
    return await getMyProfile();
  },

  updateProfile: async (profileData) => {
    // Import dynamically to avoid circular dependencies
    const { updateMyProfile } = await import("./profileService.js");
    return await updateMyProfile(profileData);
  },
};

/**
 * Decode a JWT token and return the payload
 * @param {string} token - The JWT token to decode
 * @returns {object|null} The decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding JWT:", error);
    return null;
  }
};

/**
 * Get the current user ID from the JWT token
 * @returns {string|null} The user ID from the token or null if not found
 */
export const getCurrentUserId = () => {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  if (!token) return null;

  const payload = decodeJWT(token);
  return payload?.sub || null;
};

/**
 * Who is signed in, read from the access token (never from ids left in browser storage by another account).
 * Tokens issued before student_id/teacher_id were added to them fall back to the stored user, but only when
 * its e-mail matches the token's, so a stale id from a different account is never used.
 */
export const getMyIdentity = () => {
  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");
  const claims = token ? decodeJWT(token) : null;
  let stored = {};
  try {
    stored = JSON.parse(localStorage.getItem("user") || "{}") || {};
  } catch {
    stored = {};
  }
  const sameAccount = !!(stored.email && claims?.email && stored.email === claims.email);
  const sub = claims?.sub || null;
  return {
    sub,
    userId: claims?.user_id || (sameAccount ? stored.user_id : null) || sub,
    studentId: claims?.student_id || (sameAccount ? stored.student_id : null) || null,
    teacherId: claims?.teacher_id || (sameAccount ? stored.teacher_id : null) || null,
    role: claims?.role || null,
    email: claims?.email || null,
  };
};

/** The signed-in student's student ID (falls back to the account id when the account has none). */
export const getMyStudentId = () => {
  const me = getMyIdentity();
  return me.studentId || me.sub;
};

// Start background renewal once the module loads
if (typeof window !== "undefined") {
  try {
    scheduleRenewal();
  } catch {
    /* ignore */
  }
}

export default api;
